import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { getModuleLabel } from '../shared/moduleMetadata';
import { formatRelativeTime, formatDateWithTime } from '../../../utils/formatters';
import { downloadCsv } from '../../../utils/csvExport';
import { Activity, Filter, Search, Clock, BookOpen, TrendingUp, Download } from 'lucide-react';
interface SessionAnswer {
  isCorrect: boolean;
}
interface SessionLog {
  id: string;
  accountId: string;
  subjectId: string;
  startTime: number;
  answers: SessionAnswer[];
}
export function LogsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  const sessions = useMemo(() => {
    const allSessions: SessionLog[] = [];
    allAccounts.filter(a => a.role === 'learner').forEach(learner => {
      const data = getLearnerData(learner.id);
      if (!data?.familyMastery) return;
      Object.values(data.familyMastery).forEach(mastery => {
        if (mastery.attempts > 0) {
          const subjectId = mastery.familyId.split('-')[0];
          allSessions.push({
            id: `sess-${learner.id}-${mastery.familyId}`,
            accountId: learner.id,
            subjectId: subjectId || 'UNK',
            startTime: mastery.lastSeen || Date.now() - Math.random() * 86400000 * 7,
            answers: Array.from({
              length: Math.min(10, mastery.attempts)
            }).map((_, i) => ({
              isCorrect: i < mastery.correct / mastery.attempts * Math.min(10, mastery.attempts)
            }))
          });
        }
      });
    });
    return allSessions.sort((a, b) => b.startTime - a.startTime);
  }, [allAccounts, getLearnerData]);
  const [search, setSearch] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '1d' | '7d' | '30d'>('7d');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const dateThreshold = useMemo(() => {
    const now = Date.now();
    if (dateFilter === '1d') return now - 86400000;
    if (dateFilter === '7d') return now - 7 * 86400000;
    if (dateFilter === '30d') return now - 30 * 86400000;
    return 0;
  }, [dateFilter]);
  const allSubjects = useMemo(() => Array.from(new Set(sessions.map(s => s.subjectId).filter(Boolean))).sort(), [sessions]);
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const matchDate = s.startTime >= dateThreshold;
      const matchSubject = !subjectFilter || s.subjectId === subjectFilter;
      const account = allAccounts.find(a => a.id === s.accountId);
      const matchSearch = !search || (account?.displayName ?? '').toLowerCase().includes(search.toLowerCase()) || s.subjectId?.toLowerCase().includes(search.toLowerCase()) || s.id?.toLowerCase().includes(search.toLowerCase());
      return matchDate && matchSubject && matchSearch;
    }).sort((a, b) => b.startTime - a.startTime);
  }, [sessions, allAccounts, dateThreshold, subjectFilter, search]);
  const paginatedSessions = useMemo(() => filteredSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filteredSessions, page]);
  const totalPages = Math.ceil(filteredSessions.length / PAGE_SIZE);
  const stats = useMemo(() => {
    const now = Date.now();
    const today = sessions.filter(s => s.startTime >= now - 86400000);
    const week = sessions.filter(s => s.startTime >= now - 7 * 86400000);
    const avgScore = filteredSessions.length > 0 ? Math.round(filteredSessions.reduce((sum, s) => {
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      return sum + (total > 0 ? correct / total * 100 : 0);
    }, 0) / filteredSessions.length) : 0;
    return {
      today: today.length,
      week: week.length,
      total: sessions.length,
      avgScore
    };
  }, [sessions, filteredSessions]);
  const exportCsv = () => {
    const rows = [['ID', 'Leerling', 'Vak', 'Score %', 'Vragen', 'Datum'], ...filteredSessions.map(s => {
      const account = allAccounts.find(a => a.id === s.accountId);
      const correct = s.answers?.filter(a => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      const pct = total > 0 ? Math.round(correct / total * 100) : 0;
      return [s.id, account?.displayName ?? '?', s.subjectId, pct, total, formatDateWithTime(s.startTime)];
    })];
    const csv = rows.map(r => r.join(',')).join('\n');
    downloadCsv(csv, 'sessies');
  };
  return <PageLayout title="Activiteitenlog" subtitle="Overzicht van alle oefensessies" actions={<div className="flex items-center gap-2" data-id="element-5200">
          <ConnectionBadge status="live" data-id="element-5201" />
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors bg-white shadow-sm" data-id="element-5202">
            <Download className="w-4 h-4" data-id="element-5203" /> Export CSV
          </button>
        </div>} breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Activiteitenlog'
  }]} data-id="element-5199">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-5204">
        <StatCard label="Vandaag" value={stats.today} icon={<Clock className="w-5 h-5" data-id="element-5206" />} color="blue" data-id="element-5205" />
        <StatCard label="Deze week" value={stats.week} icon={<Activity className="w-5 h-5" data-id="element-5208" />} color="emerald" data-id="element-5207" />
        <StatCard label="Totaal sessies" value={stats.total} icon={<BookOpen className="w-5 h-5" data-id="element-5210" />} color="purple" data-id="element-5209" />
        <StatCard label="Gem. score" value={`${stats.avgScore}%`} icon={<TrendingUp className="w-5 h-5" data-id="element-5212" />} color="amber" data-id="element-5211" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-5213">
        <div className="flex-1 w-full sm:max-w-sm" data-id="element-5214">
          <Input type="text" placeholder="Zoek op leerling of vak..." value={search} onChange={e => {
          setSearch(e.target.value);
          setPage(0);
        }} icon={<Search className="w-4 h-4" data-id="element-5216" />} size="sm" data-id="element-5215" />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1 sm:pb-0" data-id="element-5217">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0" data-id="element-5218">
            {(['all', '1d', '7d', '30d'] as const).map(d => <button key={d} onClick={() => {
            setDateFilter(d);
            setPage(0);
          }} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${dateFilter === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5219">
                {d === 'all' ? 'Alles' : d === '1d' ? '24u' : d === '7d' ? '7d' : '30d'}
              </button>)}
          </div>

          <div className="flex-shrink-0" data-id="element-5220">
            <Select value={subjectFilter} onChange={e => {
            setSubjectFilter(e.target.value);
            setPage(0);
          }} options={allSubjects.map(s => ({
            value: s,
            label: getModuleLabel(s) || s
          }))} placeholder="Alle vakken" icon={<Filter className="w-3.5 h-3.5" data-id="element-5222" />} fullWidth={false} size="sm" data-id="element-5221" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-5223">
        <div className="overflow-x-auto" data-id="element-5224">
          <table className="w-full text-sm text-left" data-id="element-5225">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-medium" data-id="element-5226">
              <tr data-id="element-5227">
                <th className="px-4 py-3" data-id="element-5228">Sessie ID</th>
                <th className="px-4 py-3" data-id="element-5229">Leerling</th>
                <th className="px-4 py-3" data-id="element-5230">Vak</th>
                <th className="px-4 py-3" data-id="element-5231">Score</th>
                <th className="px-4 py-3" data-id="element-5232">Tijdstip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-5233">
              {paginatedSessions.length === 0 ? <tr data-id="element-5234">
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400" data-id="element-5235">
                    Geen sessies gevonden
                  </td>
                </tr> : paginatedSessions.map(session => {
              const account = allAccounts.find(a => a.id === session.accountId);
              const correct = session.answers?.filter(a => a.isCorrect).length ?? 0;
              const total = session.answers?.length ?? 0;
              const pct = total > 0 ? Math.round(correct / total * 100) : 0;
              return <tr key={session.id} className="hover:bg-gray-50 transition-colors" data-id="element-5236">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500" data-id="element-5237">
                        {session.id.split('-').slice(0, 2).join('-')}...
                      </td>
                      <td className="px-4 py-3" data-id="element-5238">
                        <div className="flex items-center gap-2" data-id="element-5239">
                          <Avatar name={account?.displayName ?? '?'} data-id="element-5240" />
                          <span className="font-medium text-gray-900" data-id="element-5241">
                            {account?.displayName ?? 'Onbekend'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-5242">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-5243">
                          {getModuleLabel(session.subjectId) || session.subjectId}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-5244">
                        <div className="flex items-center gap-2" data-id="element-5245">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5246">
                            <div className={`h-full rounded-full ${pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                        width: `${pct}%`
                      }} data-id="element-5247" />
                          </div>
                          <span className="text-xs font-medium text-gray-700 w-8" data-id="element-5248">
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap" data-id="element-5249">
                        <div className="flex flex-col" data-id="element-5250">
                          <span data-id="element-5251">{formatDateWithTime(session.startTime)}</span>
                          <span className="text-xs text-gray-400" data-id="element-5252">
                            {formatRelativeTime(session.startTime, true)}
                          </span>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50" data-id="element-5253">
            <span className="text-xs text-gray-500" data-id="element-5254">
              Pagina {page + 1} van {totalPages} ({filteredSessions.length}{' '}
              resultaten)
            </span>
            <div className="flex gap-1" data-id="element-5255">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5256">
                Vorige
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5257">
                Volgende
              </button>
            </div>
          </div>}
      </div>
    </PageLayout>;
}